import { parse } from 'csv-parse/browser/esm/sync';
import { StagedUser } from '../services/HxcStagingService';

/**
 * Represents a parsed and validated CSV row
 */
export interface ParsedCSVRow {
  rowNumber: number;
  accountName: string;
  sfdcAccountId: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Represents an error found during CSV parsing
 */
export interface CSVError {
  row: number; // 0 for file-level errors, 1+ for row numbers
  message: string;
}

/**
 * Result of CSV parsing operation
 */
export interface CSVParseResult {
  successCount: number;
  errorCount: number;
  duplicateCount: number;
  validRows: ParsedCSVRow[];
  errors: CSVError[];
  duplicates: string[]; // Array of duplicate emails
}

/**
 * Required CSV columns (case-insensitive)
 */
const REQUIRED_COLUMNS = {
  ACCOUNT_ID: 'account id',
  ACCOUNT_NAME: 'account name',
  FIRST_NAME: 'first name',
  LAST_NAME: 'last name',
  EMAIL: 'email'
};

/**
 * Normalize column name for case-insensitive matching
 */
const normalizeColumnName = (name: string): string => {
  return name.trim().toLowerCase();
};

/**
 * Find column index by normalized name
 */
const findColumnIndex = (headers: string[], targetColumn: string): number => {
  return headers.findIndex(header => 
    normalizeColumnName(header) === targetColumn
  );
};

/**
 * Validate email format (strict validation)
 * Ensures:
 * - Valid local part (before @)
 * - Valid domain name
 * - TLD contains only letters (no numbers like .com123)
 * - TLD is at least 2 characters
 */
const isValidEmail = (email: string): boolean => {
  // Strict email regex that ensures TLD only contains letters
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(email)) {
    return false;
  }
  
  // Additional check: ensure no numbers after the last dot in domain
  const domainPart = email.split('@')[1];
  if (!domainPart) {
    return false;
  }
  
  const tld = domainPart.split('.').pop();
  if (!tld || /\d/.test(tld)) {
    // TLD contains numbers, invalid
    return false;
  }
  
  return true;
};

/**
 * Check if a row is completely empty (all required fields are missing)
 */
const isEmptyRow = (
  accountId: string,
  accountName: string,
  firstName: string,
  lastName: string,
  email: string
): boolean => {
  return !accountId && !accountName && !firstName && !lastName && !email;
};

/**
 * Parse and validate a CSV file
 * @param file - The CSV file to parse
 * @param existingUsers - Array of existing staged users to check for duplicates
 * @returns Promise containing parse results
 */
export const parseCSV = async (
  file: File,
  existingUsers: StagedUser[]
): Promise<CSVParseResult> => {
  return new Promise((resolve) => {
    const validRows: ParsedCSVRow[] = [];
    const errors: CSVError[] = [];
    const duplicates: string[] = [];
    
    // Create a set of existing emails for fast duplicate checking (case-insensitive)
    const existingEmails = new Set(
      existingUsers.map(user => user.email.toLowerCase())
    );

    // Read file as text
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const csvText = event.target?.result as string;
        
        if (!csvText || csvText.trim().length === 0) {
          errors.push({
            row: 0,
            message: 'File is empty'
          });
          
          resolve({
            successCount: 0,
            errorCount: 1,
            duplicateCount: 0,
            validRows: [],
            errors,
            duplicates: []
          });
          return;
        }

        // Parse CSV using synchronous API
        let records: any[];
        
        try {
          records = parse(csvText, {
            columns: true, // Use first row as headers
            skip_empty_lines: true,
            trim: true,
            relax_column_count: true // Allow rows with different column counts
          });
        } catch (parseError: any) {
          errors.push({
            row: 0,
            message: `Failed to parse CSV: ${parseError.message}`
          });
          
          resolve({
            successCount: 0,
            errorCount: 1,
            duplicateCount: 0,
            validRows: [],
            errors,
            duplicates: []
          });
          return;
        }

        // Check if we have any records
        if (!records || records.length === 0) {
          errors.push({
            row: 0,
            message: 'No data rows found in CSV file'
          });
          
          resolve({
            successCount: 0,
            errorCount: 1,
            duplicateCount: 0,
            validRows: [],
            errors,
            duplicates: []
          });
          return;
        }

        // Get headers from first record
        const headers = Object.keys(records[0]);
        
        // Find required column indices
        const accountIdIdx = findColumnIndex(headers, REQUIRED_COLUMNS.ACCOUNT_ID);
        const accountNameIdx = findColumnIndex(headers, REQUIRED_COLUMNS.ACCOUNT_NAME);
        const firstNameIdx = findColumnIndex(headers, REQUIRED_COLUMNS.FIRST_NAME);
        const lastNameIdx = findColumnIndex(headers, REQUIRED_COLUMNS.LAST_NAME);
        const emailIdx = findColumnIndex(headers, REQUIRED_COLUMNS.EMAIL);

        // Validate that all required columns exist
        const missingColumns: string[] = [];
        if (accountIdIdx === -1) missingColumns.push('Account ID');
        if (accountNameIdx === -1) missingColumns.push('Account Name');
        if (firstNameIdx === -1) missingColumns.push('First Name');
        if (lastNameIdx === -1) missingColumns.push('Last Name');
        if (emailIdx === -1) missingColumns.push('Email');

        if (missingColumns.length > 0) {
          errors.push({
            row: 0,
            message: `Missing required columns: ${missingColumns.join(', ')}`
          });
          
          resolve({
            successCount: 0,
            errorCount: 1,
            duplicateCount: 0,
            validRows: [],
            errors,
            duplicates: []
          });
          return;
        }

        // Get actual column names for data extraction
        const accountIdCol = headers[accountIdIdx];
        const accountNameCol = headers[accountNameIdx];
        const firstNameCol = headers[firstNameIdx];
        const lastNameCol = headers[lastNameIdx];
        const emailCol = headers[emailIdx];

        // Process each row
        records.forEach((record, index) => {
          const rowNumber = index + 2; // +2 because: +1 for header row, +1 for 1-based indexing
          const rowErrors: string[] = [];

          // Extract values
          const accountId = record[accountIdCol]?.trim() || '';
          const accountName = record[accountNameCol]?.trim() || '';
          const firstName = record[firstNameCol]?.trim() || '';
          const lastName = record[lastNameCol]?.trim() || '';
          const email = record[emailCol]?.trim() || '';

          // Check if row is completely empty - if so, ignore it silently
          if (isEmptyRow(accountId, accountName, firstName, lastName, email)) {
            return; // Skip this row without counting as error
          }

          // Validate required fields (only if row has some data)
          if (!accountId) {
            rowErrors.push('missing Account ID');
          }
          if (!accountName) {
            rowErrors.push('missing Account Name');
          }
          if (!firstName) {
            rowErrors.push('missing First Name');
          }
          if (!lastName) {
            rowErrors.push('missing Last Name');
          }
          if (!email) {
            rowErrors.push('missing Email');
          } else if (!isValidEmail(email)) {
            rowErrors.push('invalid Email format');
          }

          // Check for duplicate email
          const isDuplicate = existingEmails.has(email.toLowerCase());

          // If there are validation errors, add to errors list
          if (rowErrors.length > 0) {
            errors.push({
              row: rowNumber,
              message: rowErrors.join(', ')
            });
          } else if (isDuplicate) {
            // If it's a duplicate, add to duplicates list
            duplicates.push(email);
          } else {
            // Valid row, add to results
            validRows.push({
              rowNumber,
              accountName,
              sfdcAccountId: accountId,
              firstName,
              lastName,
              email
            });
          }
        });

        // Resolve with results
        resolve({
          successCount: validRows.length,
          errorCount: errors.length,
          duplicateCount: duplicates.length,
          validRows,
          errors,
          duplicates
        });

        // Clear records array to free memory
        records.length = 0;
      } catch (error) {
        errors.push({
          row: 0,
          message: error instanceof Error ? error.message : 'Unknown error occurred'
        });
        
        resolve({
          successCount: 0,
          errorCount: 1,
          duplicateCount: 0,
          validRows: [],
          errors,
          duplicates: []
        });
      }
    };

    reader.onerror = () => {
      errors.push({
        row: 0,
        message: 'Failed to read file'
      });
      
      resolve({
        successCount: 0,
        errorCount: 1,
        duplicateCount: 0,
        validRows: [],
        errors,
        duplicates: []
      });
    };

    // Start reading the file
    reader.readAsText(file);
  });
};
